/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetWorkloadCustomFlowsRequest,
  GetWorkloadCustomFlowsSecurity,
} from "../models/operations/getworkloadcustomflows.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildWorkloadCustomFlowsQuery,
  prefetchWorkloadCustomFlows,
  queryKeyWorkloadCustomFlows,
  WorkloadCustomFlowsQueryData,
} from "./workloadCustomFlows.core.js";
export {
  buildWorkloadCustomFlowsQuery,
  prefetchWorkloadCustomFlows,
  queryKeyWorkloadCustomFlows,
  type WorkloadCustomFlowsQueryData,
};

export type WorkloadCustomFlowsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getCustomFlows workloadIdentities
 *
 * @remarks
 * Get the forms for trusting a platform the catalog does not list and allowing its workloads: registering and editing a trusted platform, and allowing and editing access. The same for every organization. Requires workload:read.
 */
export function useWorkloadCustomFlows(
  request?: GetWorkloadCustomFlowsRequest | undefined,
  security?: GetWorkloadCustomFlowsSecurity | undefined,
  options?: QueryHookOptions<
    WorkloadCustomFlowsQueryData,
    WorkloadCustomFlowsQueryError
  >,
): UseQueryResult<WorkloadCustomFlowsQueryData, WorkloadCustomFlowsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildWorkloadCustomFlowsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getCustomFlows workloadIdentities
 *
 * @remarks
 * Get the forms for trusting a platform the catalog does not list and allowing its workloads: registering and editing a trusted platform, and allowing and editing access. The same for every organization. Requires workload:read.
 */
export function useWorkloadCustomFlowsSuspense(
  request?: GetWorkloadCustomFlowsRequest | undefined,
  security?: GetWorkloadCustomFlowsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    WorkloadCustomFlowsQueryData,
    WorkloadCustomFlowsQueryError
  >,
): UseSuspenseQueryResult<
  WorkloadCustomFlowsQueryData,
  WorkloadCustomFlowsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildWorkloadCustomFlowsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setWorkloadCustomFlowsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: WorkloadCustomFlowsQueryData,
): WorkloadCustomFlowsQueryData | undefined {
  const key = queryKeyWorkloadCustomFlows(...queryKeyBase);

  return client.setQueryData<WorkloadCustomFlowsQueryData>(key, data);
}

export function invalidateWorkloadCustomFlows(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "workloadIdentities",
      "getCustomFlows",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllWorkloadCustomFlows(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "workloadIdentities", "getCustomFlows"],
  });
}
